import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import dayjs from '~/timeInit';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  type SxProps,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  type Theme,
  Tooltip,
  Typography,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import {
  useApproveExtranetChange,
  useBulkApproveExtranetChanges,
  useBulkRejectExtranetChanges,
  useGetExtranetPendingChanges,
  useRejectExtranetChange,
} from '~/hooks/apis/extranet/useExtranetPendingChanges';

import {
  ExtranetPendingChange,
  ExtranetPendingChangeRequestType,
  ExtranetPendingChangeStatus,
  ListExtranetPendingChangesParams,
} from '~/services/ExtranetService';

import ApiError from '~/types/ApiError';

import ValueDiff from './ValueDiff';

const REQUEST_TYPE_LABELS: Record<ExtranetPendingChangeRequestType, string> = {
  property: 'Property',
  property_meta: 'Property',
  property_taxes: 'Property',
  property_facility: 'Property',
  property_image: 'Property',
  room_type: 'Rooms',
  room_availability: 'Rooms',
  rate_plan: 'Rooms',
  promotional_discount: 'Rooms',
};

const REQUEST_TYPE_OPTIONS: { value: ExtranetPendingChangeRequestType; label: string }[] = [
  { value: 'property', label: 'Property' },
  { value: 'property_meta', label: 'Property Meta' },
  { value: 'property_taxes', label: 'Property Taxes' },
  { value: 'property_facility', label: 'Property Facility' },
  { value: 'property_image', label: 'Property Image' },
  { value: 'room_type', label: 'Room Type' },
  { value: 'room_availability', label: 'Room Availability' },
  { value: 'rate_plan', label: 'Rate Plan' },
  { value: 'promotional_discount', label: 'Promotional Discount' },
];

const TAB_CHIP_COLORS: Record<string, SxProps<Theme>> = {
  Rooms: { bgcolor: 'info.light', color: 'info.contrastText' },
  Property: { bgcolor: 'warning.light', color: 'warning.contrastText' },
};

const STATUS_CHIP_COLOR: Record<ExtranetPendingChangeStatus, 'warning' | 'success' | 'error' | 'default'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  superseded: 'default',
};

const HEADER_CELL_SX: SxProps<Theme> = {
  fontWeight: 700,
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'text.secondary',
};

const PAGE_SIZE = 50;

interface Filters {
  status: string;
  vendor_id: string;
  request_type: string;
  property_id: string;
}

const DEFAULT_FILTERS: Filters = { status: 'pending', vendor_id: '', request_type: '', property_id: '' };

const REQUEST_TYPE_DETAIL_LABELS: Record<ExtranetPendingChangeRequestType, string> = Object.fromEntries(
  REQUEST_TYPE_OPTIONS.map(({ value, label }) => [value, label]),
) as Record<ExtranetPendingChangeRequestType, string>;

function buildParams(filters: Filters, page: number, statusOverride?: string): ListExtranetPendingChangesParams {
  const params: ListExtranetPendingChangesParams = { limit: PAGE_SIZE, page };
  const status = statusOverride ?? filters.status;
  if (status) params.status = status;
  if (filters.vendor_id) params.vendor_id = filters.vendor_id;
  if (filters.request_type) params.request_type = filters.request_type as ExtranetPendingChangeRequestType;
  if (filters.property_id) params.property_id = filters.property_id;
  return params;
}

function StatCard({ label, value, hint }: { label: string; value: string | number; hint?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, minWidth: 160 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight={700} mt={0.5}>
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      )}
    </Paper>
  );
}

function FilterToolbar({
  filters,
  setFilters,
  showStatusFilter,
  onSearch,
  onClear,
}: {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  showStatusFilter: boolean;
  onSearch: () => void;
  onClear: () => void;
}) {
  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap" alignItems="flex-end" mb={2}>
      <TextField
        size="small"
        label="Partner ID"
        value={filters.vendor_id}
        onChange={(e) => setFilters((f) => ({ ...f, vendor_id: e.target.value }))}
        sx={{ width: 180 }}
      />
      <TextField
        size="small"
        label="Property ID"
        value={filters.property_id}
        onChange={(e) => setFilters((f) => ({ ...f, property_id: e.target.value }))}
        sx={{ width: 160 }}
      />
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel>Tab</InputLabel>
        <Select
          label="Tab"
          value={filters.request_type}
          onChange={(e) => setFilters((f) => ({ ...f, request_type: e.target.value }))}
        >
          <MenuItem value="">All tabs</MenuItem>
          {REQUEST_TYPE_OPTIONS.map(({ value, label }) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {showStatusFilter && (
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="superseded">Superseded</MenuItem>
          </Select>
        </FormControl>
      )}
      <Stack direction="row" spacing={1}>
        <Button variant="contained" onClick={onSearch}>
          Search
        </Button>
        <Button variant="text" onClick={onClear}>
          Clear
        </Button>
      </Stack>
    </Stack>
  );
}

function ChangeRow({
  row,
  showActions,
  showStatus,
  selected,
  isMutating,
  onToggleSelect,
  onViewDiff,
  onAction,
}: {
  row: ExtranetPendingChange;
  showActions: boolean;
  showStatus: boolean;
  selected: boolean;
  isMutating: boolean;
  onToggleSelect: () => void;
  onViewDiff: () => void;
  onAction: (type: 'approve' | 'reject') => void;
}) {
  const tabLabel = REQUEST_TYPE_LABELS[row.request_type] ?? row.request_type;
  const tabStyle = TAB_CHIP_COLORS[tabLabel] ?? {};

  return (
    <TableRow hover sx={{ verticalAlign: 'middle' }}>
      {showActions && (
        <TableCell padding="checkbox" sx={{ py: 1 }}>
          {row.status === 'pending' && <Checkbox checked={selected} onChange={onToggleSelect} size="small" />}
        </TableCell>
      )}
      <TableCell sx={{ whiteSpace: 'nowrap', py: 1 }}>
        <Typography variant="body2">{dayjs(row.created_at).format('D/M/YY, h:mm a')}</Typography>
      </TableCell>
      <TableCell sx={{ py: 1, overflow: 'hidden' }}>
        <Typography variant="body2" fontWeight={600} noWrap sx={{ lineHeight: 1.3 }}>
          {row.fk_vendor_id}
        </Typography>
        <Tooltip title={row.target_ids?.property_id ?? ''}>
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ fontFamily: 'monospace', lineHeight: 1.3, display: 'block' }}
          >
            {row.target_ids?.property_id ?? '—'}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ py: 1, overflow: 'hidden' }}>
        <Tooltip title={row.requested_by_user_id}>
          <Typography variant="body2" noWrap sx={{ fontFamily: 'monospace', fontSize: 12 }}>
            {row.requested_by_user_id}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell sx={{ py: 1 }}>
        <Chip size="small" label={tabLabel} sx={{ ...tabStyle, fontWeight: 600, fontSize: 11 }} />
      </TableCell>
      <TableCell sx={{ maxWidth: 400, py: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              fontFamily: 'monospace',
              fontSize: 12,
              bgcolor: 'grey.50',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              px: 1,
              py: 0.5,
              maxHeight: 80,
              overflow: 'auto',
              flex: 1,
            }}
          >
            <ValueDiff previous={row.previous_value} proposed={row.proposed_value} compact />
          </Box>
          <Button
            size="small"
            variant="text"
            sx={{ px: 0, textTransform: 'uppercase', fontSize: 11, whiteSpace: 'nowrap', minWidth: 'auto' }}
            onClick={onViewDiff}
          >
            View diff
          </Button>
        </Stack>
      </TableCell>
      {showActions && (
        <TableCell sx={{ py: 1 }}>
          {row.status === 'pending' ? (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Button size="small" variant="contained" disabled={isMutating} onClick={() => onAction('approve')}>
                Approve
              </Button>
              <Button
                size="small"
                variant="text"
                color="error"
                disabled={isMutating}
                onClick={() => onAction('reject')}
              >
                Reject
              </Button>
            </Stack>
          ) : (
            <Chip size="small" label={row.status} color={STATUS_CHIP_COLOR[row.status]} />
          )}
        </TableCell>
      )}
      {showStatus && (
        <TableCell sx={{ py: 1, overflow: 'hidden' }}>
          <Chip size="small" label={row.status} color={STATUS_CHIP_COLOR[row.status]} />
          {row.reviewed_by_user_id && (
            <Tooltip title={row.reviewed_by_user_id}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                noWrap
                sx={{ fontFamily: 'monospace', fontSize: 10, mt: 0.5 }}
              >
                by {row.reviewed_by_user_id}
              </Typography>
            </Tooltip>
          )}
          {row.reviewed_at && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: 10 }}>
              {dayjs(row.reviewed_at).format('D/M/YY')}
            </Typography>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}

function ConfirmActionDialog({
  action,
  isMutating,
  onClose,
  onConfirm,
}: {
  action: { type: 'approve' | 'reject'; ids: string[] } | null;
  isMutating: boolean;
  onClose: () => void;
  onConfirm: (action: { type: 'approve' | 'reject'; ids: string[] }) => void;
}) {
  if (!action) return null;
  const isApprove = action.type === 'approve';
  const label = isApprove ? 'Approve' : 'Reject';
  const count = action.ids.length;

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle>Confirm {isApprove ? 'Approval' : 'Rejection'}</DialogTitle>
      <DialogContent>
        <Typography>
          {label} {count === 1 ? 'this change' : `${count} changes`}?
          {isApprove && ' This will mark the change as approved.'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={isApprove ? 'primary' : 'error'}
          disabled={isMutating}
          onClick={() => onConfirm(action)}
          startIcon={isMutating ? <CircularProgress size={14} /> : undefined}
        >
          {label}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function ExtranetApprovalsPage() {
  const { enqueueSnackbar } = useSnackbar();

  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<ListExtranetPendingChangesParams>({
    status: 'pending',
    limit: PAGE_SIZE,
    page: 1,
  });
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [diffRow, setDiffRow] = useState<ExtranetPendingChange | null>(null);
  const [confirmAction, setConfirmAction] = useState<{ type: 'approve' | 'reject'; ids: string[] } | null>(null);

  const { data, isLoading, error } = useGetExtranetPendingChanges(appliedFilters);
  const { data: pendingCountData } = useGetExtranetPendingChanges({ status: 'pending', limit: 0 });

  const isPendingTab = activeTab === 0;
  const isHistoryTab = activeTab === 1;

  useEffect(() => {
    setPage(1);
    setSelected(new Set());
    const nextStatus = activeTab === 0 ? 'pending' : '';
    const resetFilters = { ...DEFAULT_FILTERS, status: nextStatus };
    setFilters(resetFilters);
    setAppliedFilters(buildParams(resetFilters, 1));
  }, [activeTab]);

  const approveOne = useApproveExtranetChange();
  const rejectOne = useRejectExtranetChange();
  const bulkApprove = useBulkApproveExtranetChanges();
  const bulkReject = useBulkRejectExtranetChanges();

  const rows = data?.result ?? [];
  const total = data?.total ?? 0;
  const selectableRows = isPendingTab ? rows : [];

  const handleSearch = useCallback(() => {
    setPage(1);
    setSelected(new Set());
    setAppliedFilters(buildParams(filters, 1));
  }, [filters]);

  const handleClear = useCallback(() => {
    const nextStatus = activeTab === 0 ? 'pending' : '';
    const resetFilters = { ...DEFAULT_FILTERS, status: nextStatus };
    setFilters(resetFilters);
    setPage(1);
    setSelected(new Set());
    setAppliedFilters(buildParams(resetFilters, 1));
  }, [activeTab]);

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    setSelected((prev) => {
      if (prev.size === selectableRows.length) return new Set();
      return new Set(selectableRows.map((r) => r.id));
    });
  }, [selectableRows]);

  const handleConfirmAction = useCallback(
    async (action: { type: 'approve' | 'reject'; ids: string[] }) => {
      const { type, ids } = action;
      setConfirmAction(null);

      try {
        if (ids.length === 1) {
          if (type === 'approve') {
            await approveOne.mutateAsync(ids[0]);
          } else {
            await rejectOne.mutateAsync(ids[0]);
          }
          enqueueSnackbar(`Change ${type}d`, { variant: 'success' });
        } else {
          const result = type === 'approve' ? await bulkApprove.mutateAsync(ids) : await bulkReject.mutateAsync(ids);
          const succeeded = type === 'approve' ? result.approved ?? 0 : result.rejected ?? 0;
          enqueueSnackbar(`${succeeded} change(s) ${type}d, ${result.skipped} skipped`, { variant: 'success' });
        }
        setSelected(new Set());
      } catch (err) {
        if (err instanceof ApiError && err.status === 409) {
          const cleanMessage = err.message.replace(/^Network error received \(status code \d+\): /, '');
          enqueueSnackbar(`Already processed: ${cleanMessage}`, { variant: 'warning' });
        } else if (err instanceof Error) {
          enqueueSnackbar(`Failed to ${type}: ${err.message}`, { variant: 'error' });
        }
      }
    },
    [approveOne, rejectOne, bulkApprove, bulkReject, enqueueSnackbar],
  );

  const isMutating = approveOne.isPending || rejectOne.isPending || bulkApprove.isPending || bulkReject.isPending;
  const selectedIds = Array.from(selected);
  const allPendingSelected = selectableRows.length > 0 && selected.size === selectableRows.length;
  const colCount = isPendingTab ? 7 : 6;

  return (
    <Box>
      <PageSubheader title="Extranet — change approvals" />

      <Typography variant="body2" color="text.secondary" mb={2} maxWidth={680}>
        Review pending edits to <strong>Rooms</strong> and <strong>Property</strong> submitted by partners. Approve to
        mark the change as reviewed. (Changes are not yet applied to live data.)
      </Typography>

      <Stack direction="row" spacing={2} flexWrap="wrap" mb={3}>
        <StatCard label="Pending changes" value={pendingCountData?.total ?? 0} hint="Rooms & Property" />
      </Stack>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Pending approvals" />
        <Tab label="History" />
      </Tabs>

      <FilterToolbar
        filters={filters}
        setFilters={setFilters}
        showStatusFilter={isHistoryTab}
        onSearch={handleSearch}
        onClear={handleClear}
      />

      {selectedIds.length > 0 && (
        <Stack direction="row" spacing={1} mb={1.5} alignItems="center">
          <Typography variant="body2">{selectedIds.length} selected</Typography>
          <Button
            size="small"
            variant="contained"
            disabled={isMutating}
            onClick={() => setConfirmAction({ type: 'approve', ids: selectedIds })}
          >
            Bulk approve ({selectedIds.length})
          </Button>
          <Button
            size="small"
            variant="text"
            color="error"
            disabled={isMutating}
            onClick={() => setConfirmAction({ type: 'reject', ids: selectedIds })}
          >
            Bulk reject
          </Button>
        </Stack>
      )}

      {error instanceof Error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.message}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              {isPendingTab && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allPendingSelected}
                    indeterminate={selected.size > 0 && !allPendingSelected}
                    onChange={toggleSelectAll}
                    inputProps={{ 'aria-label': 'Select all pending changes' }}
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                  />
                </TableCell>
              )}
              <TableCell sx={{ ...HEADER_CELL_SX, width: 120 }}>Submitted</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, width: '18%' }}>Partner / Property</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, width: '18%' }}>Changed by</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, width: 70 }}>Tab</TableCell>
              <TableCell sx={HEADER_CELL_SX}>Change (old → new)</TableCell>
              {isPendingTab && <TableCell sx={{ ...HEADER_CELL_SX, width: 160 }}>Action</TableCell>}
              {isHistoryTab && <TableCell sx={{ ...HEADER_CELL_SX, width: 160 }}>Status</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={colCount} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No records found
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <ChangeRow
                key={row.id}
                row={row}
                showActions={isPendingTab}
                showStatus={isHistoryTab}
                selected={selected.has(row.id)}
                isMutating={isMutating}
                onToggleSelect={() => toggleSelect(row.id)}
                onViewDiff={() => setDiffRow(row)}
                onAction={(type) => setConfirmAction({ type, ids: [row.id] })}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
        onPageChange={(_, p) => {
          const nextPage = p + 1;
          setPage(nextPage);
          setSelected(new Set());
          setAppliedFilters((prev) => ({ ...prev, page: nextPage }));
        }}
      />

      <Dialog open={!!diffRow} onClose={() => setDiffRow(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          Change diff — {diffRow && (REQUEST_TYPE_DETAIL_LABELS[diffRow.request_type] ?? diffRow.request_type)}
        </DialogTitle>
        {diffRow && (
          <Box sx={{ px: 3, pb: 1, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Partner
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {diffRow.fk_vendor_id}
              </Typography>
            </Box>
            {diffRow.target_ids?.property_id && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Property
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {diffRow.target_ids.property_id}
                </Typography>
              </Box>
            )}
            <Box>
              <Typography variant="caption" color="text.secondary">
                Submitted
              </Typography>
              <Typography variant="body2">{dayjs(diffRow.created_at).format('D/M/YY, h:mm a')}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Changed by
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                {diffRow.requested_by_user_id}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Status
              </Typography>
              <Box>
                <Chip size="small" label={diffRow.status} color={STATUS_CHIP_COLOR[diffRow.status]} />
              </Box>
            </Box>
            {diffRow.reviewed_by_user_id && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Reviewed by
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                  {diffRow.reviewed_by_user_id}
                </Typography>
              </Box>
            )}
            {diffRow.reviewed_at && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Reviewed at
                </Typography>
                <Typography variant="body2">{dayjs(diffRow.reviewed_at).format('D/M/YY, h:mm a')}</Typography>
              </Box>
            )}
          </Box>
        )}
        <DialogContent dividers>
          {diffRow && <ValueDiff previous={diffRow.previous_value} proposed={diffRow.proposed_value} />}
        </DialogContent>
        <DialogActions>
          {diffRow?.status === 'pending' && (
            <>
              <Button
                variant="contained"
                onClick={() => {
                  setConfirmAction({ type: 'approve', ids: [diffRow.id] });
                  setDiffRow(null);
                }}
              >
                Approve
              </Button>
              <Button
                variant="text"
                color="error"
                onClick={() => {
                  setConfirmAction({ type: 'reject', ids: [diffRow.id] });
                  setDiffRow(null);
                }}
              >
                Reject
              </Button>
            </>
          )}
          <Button variant="text" onClick={() => setDiffRow(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmActionDialog
        action={confirmAction}
        isMutating={isMutating}
        onClose={() => setConfirmAction(null)}
        onConfirm={handleConfirmAction}
      />
    </Box>
  );
}
